import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import uuid from 'react-uuid';
import Loader from '../../components/common/loader';
import Button from '../../components/Elements/button/button';
import PageHeader from '../../components/page-header';
import { addToast } from '../../store/features/toastSlice';
import {
  archiveTemplateVersion,
  duplicateTemplateVersion,
  getTemplateDetails,
  getTemplateVersions,
  publishTemplateVersion,
  rePublishTemplateVersion,
  setTemplateDetails,
  setTemplateVersions,
} from '../../store/features/templatesSlice';
import { TemplateVersionsWrapper } from '../templates/templates.styled';
import TemplateOrganizations from './template-organizations';

const PAGE_SIZE = 13;

const formatDate = value => (value ? moment.unix(value).format('DD/MM/YYYY HH:mm') : '—');

/**
 * Template versions, mirroring the form version log: one published version at a time, with
 * duplicate / publish / archive / revert on each row.
 */
const TemplateDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { templateDetails, templateVersions } = useSelector(state => state.templates);
  const [selectedPage, setSelectedPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('VERSIONS');

  const fetchVersions = currentPage => {
    setLoading(true);
    dispatch(
      getTemplateVersions({
        id: templateId,
        params: { page: currentPage, size: PAGE_SIZE, sort_by: 'version_no', order_by: 'DESC' },
      }),
    )
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    dispatch(getTemplateDetails({ id: templateId })).catch(() => {});
    return () => {
      dispatch(setTemplateDetails({}));
      dispatch(setTemplateVersions({ data: {} }));
    };
  }, [templateId]);

  useEffect(() => {
    fetchVersions(selectedPage);
  }, [templateId, selectedPage]);

  const runAction = async (action, successText, failureText) => {
    try {
      await dispatch(action);
      dispatch(addToast({ error: false, text: successText, id: uuid() }));
      fetchVersions(selectedPage);
    } catch (e) {
      dispatch(addToast({ error: true, text: failureText, id: uuid() }));
    }
  };

  const onDuplicate = version =>
    runAction(
      duplicateTemplateVersion({ templateId, versionId: version.id }),
      `V${version.version_no} duplicated`,
      'Could not duplicate this version',
    );

  const onPublish = version =>
    runAction(
      publishTemplateVersion({ templateId, versionId: version.id }),
      `V${version.version_no} published`,
      'Could not publish this version',
    );

  const onArchive = version =>
    runAction(
      archiveTemplateVersion({ templateId, versionId: version.id }),
      `V${version.version_no} archived`,
      'Could not archive this version',
    );

  const onRevert = version =>
    runAction(
      rePublishTemplateVersion({ templateId, versionId: version.id }),
      `Reverted to V${version.version_no}`,
      'Could not revert to this version',
    );

  const statusOf = version => {
    if (version.archived_on) return { className: 'archived', text: 'Archived' };
    if (version.published_on) return { className: 'published', text: 'Published' };
    return { className: 'draft', text: 'Draft' };
  };

  const versions = templateVersions?.content || [];

  return (
    <TemplateVersionsWrapper className="flex-column flex-1">
      <PageHeader
        names={[
          { text: 'Templates', path: '/templates' },
          { text: templateDetails?.name, onClick: () => {} },
        ]}
        showPagination={tab === 'VERSIONS'}
        pagination={{
          selectedPage: selectedPage,
          totalPages: templateVersions?.total_pages || 0,
          setSelectedPage: setSelectedPage,
        }}
      />

      <div className="flex items-center col-gap-6 mt-4 mx-6">
        {['VERSIONS', 'ORGANISATIONS'].map(name => (
          <label
            key={name}
            className="cursor regular-text"
            style={{ fontWeight: tab === name ? 700 : 400 }}
            onClick={() => setTab(name)}>
            {name === 'VERSIONS' ? 'Versions' : 'Organisations'}
          </label>
        ))}
      </div>

      {tab === 'ORGANISATIONS' ? (
        <TemplateOrganizations templateId={templateId} />
      ) : (
        <div className="flex-column flex-1 mt-6 mx-6 overflow-auto">
          <div className="list-header pxy-4">
            <label className="medium-text">Version</label>
            <label className="medium-text">Created</label>
            <label className="medium-text">Published</label>
            <label className="medium-text">Status</label>
            <label className="medium-text">Actions</label>
          </div>

          {loading ? (
            <Loader />
          ) : versions.length === 0 ? (
            <label className="muted regular-text mt-10 flex justify-center">No versions yet</label>
          ) : (
            versions.map(version => {
              const status = statusOf(version);
              const isPublished = Boolean(version.published_on && !version.archived_on);
              return (
                <div key={version.id} className="list-row pxy-4">
                  <label className="medium-text">{`V${version.version_no}`}</label>
                  <label className="muted regular-text">{formatDate(version.created_on)}</label>
                  <label className="muted regular-text">{formatDate(version.published_on)}</label>
                  <label className={`status-pill regular-text ${status.className}`}>{status.text}</label>
                  <div className="flex items-center col-gap-2">
                    <Button
                      className="secondary"
                      size="small"
                      label="Edit"
                      onClick={() => navigate(`/templates/${templateId}/versions/${version.id}`)}
                    />
                    <Button className="secondary" size="small" label="Duplicate" onClick={() => onDuplicate(version)} />
                    {isPublished ? (
                      <Button className="secondary" size="small" label="Archive" onClick={() => onArchive(version)} />
                    ) : version.archived_on ? (
                      <Button className="secondary" size="small" label="Revert" onClick={() => onRevert(version)} />
                    ) : (
                      <Button className="primary" size="small" label="Publish" onClick={() => onPublish(version)} />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </TemplateVersionsWrapper>
  );
};

export default TemplateDetails;
